import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, EmptyState, Select, Skeleton, StatusPill, useToast } from '../../design-system';
import '../styles/ClientRequests.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'closed', label: 'Closed' },
];

// The firm's side of the client conversation.
//
// The backend for this existed for a long time with no UI at all, which meant
// requests could be created by API and then never seen. Now it's also the
// inbox for anything a client raises through the portal.
function OrgAdminClientRequests() {
  const revision = useLiveRefresh();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(searchParams.get('status') || 'open');
  const [onlyUnread, setOnlyUnread] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = status === 'all' ? {} : { status };
      const res = await apiClient.get('/api/client-requests', { params });
      setRequests(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load client requests');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, revision]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => (onlyUnread ? requests.filter((r) => r.hasUnreadForStaff) : requests),
    [requests, onlyUnread]
  );

  const unreadCount = requests.filter((r) => r.hasUnreadForStaff).length;

  return (
    <OrgAdminLayout
      title="Client Requests"
      subtitle="What you've asked clients for, and what they've asked you"
    >
      <div className="cr-toolbar">
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="cr-filter">
          {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>

        {/* The queue's real job is "what has the client said that I haven't
            read?" — so that filter is one click, not a search. */}
        <button
          type="button"
          className={`cr-chip${onlyUnread ? ' is-on' : ''}`}
          onClick={() => setOnlyUnread((v) => !v)}
        >
          Needs your attention {unreadCount > 0 && <span className="cr-chip-count">{unreadCount}</span>}
        </button>

        <div className="cr-toolbar-spacer" />
        <Button variant="primary" onClick={() => navigate('/org-admin/client-requests/new')}>
          + Request something
        </Button>
      </div>

      {loading ? (
        <Card><Skeleton.Text lines={5} /></Card>
      ) : visible.length === 0 ? (
        <EmptyState
          icon="💬"
          title={onlyUnread ? 'Nothing waiting on you' : 'No client requests'}
          description="Ask a client for documents or information, and the conversation lives here."
        />
      ) : (
        <div className="cr-list">
          {visible.map((r) => (
            <button
              key={r._id}
              type="button"
              className={`cr-row${r.hasUnreadForStaff ? ' is-unread' : ''}`}
              onClick={() => navigate(`/org-admin/client-requests/${r._id}`)}
            >
              <div className="cr-row-main">
                <span className="cr-row-title">
                  {r.hasUnreadForStaff && <span className="cr-dot" title="New from the client" />}
                  {r.title}
                </span>
                <span className="cr-row-meta">
                  {r.clientId?.displayName || 'Unknown client'}
                  {/* Direction matters: one is work you asked for, the other is
                      an inbound question you owe an answer to. */}
                  {r.createdByClientUserId ? ' · raised by the client' : ''}
                  {r.dueDate ? ` · due ${fmtDate(r.dueDate)}` : ''}
                </span>
              </div>
              <div className="cr-row-right">
                <StatusPill status={r.priority} />
                <StatusPill status={r.status} />
              </div>
            </button>
          ))}
        </div>
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminClientRequests;
